import React, { ChangeEvent, FC } from 'react'
import styled from 'styled-components'
import { primaryColor } from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fsXS,
  fwMediumBold,
} from 'uiComponents/constants/typography'

const DURATION = 150

type Props = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'title'
> & { className?: string }

/**
 * This is copied from tadarida ui-components due to issues with react-switch styles in Vite
 */
const ToggleImpl: React.FC<Props> = ({ className, checked, ...rest }) => {
  return (
    <Wrapper
      className={className}
      data-testid="toggleWrapper"
      data-checked={checked}
    >
      <Input data-testid="toggle" type="checkbox" checked={checked} {...rest} />
      <Track data-testid="toggleTrack" />
    </Wrapper>
  )
}

const Track = styled.div`
  background-color: #e5e5e5;
  border-radius: 0.4375rem;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`

const Wrapper = styled.div`
  box-sizing: border-box;
  height: 0.875rem;
  position: relative;
  width: 2rem;

  @media (pointer: fine) {
    &:before {
      content: '';
      background-color: rgba(0, 0, 0, 0.05);
      border-radius: 100%;
      box-sizing: border-box;
      content: '';
      height: 3rem;
      left: -0.875rem;
      opacity: 0;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      // eslint-disable-next-line local-rules/css-layout-animation
      transition: left ${DURATION}ms ease, opacity ${DURATION}ms ease;
      visibility: visible;
      width: 3rem;
      z-index: 1;
    }
  }

  &:after {
    background-color: #ffffff;
    border-radius: 100%;
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.24), 0 0 2px 0 rgba(0, 0, 0, 0.12);
    box-sizing: border-box;
    content: '';
    height: 1.25rem;
    left: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    // eslint-disable-next-line local-rules/css-layout-animation
    transition: left ${DURATION}ms ease;
    visibility: visible;
    width: 1.25rem;
    z-index: 1;
  }

  &:hover {
    &:before {
      opacity: 1;
    }
  }

  &[data-checked='true'] {
    ${Track} {
      background-color: #70eab4;
    }

    @media (pointer: fine) {
      &:before {
        left: -0.125rem;
      }

      &:hover {
        &:before {
          background-color: rgba(113, 225, 191, 0.1);
        }
      }
    }

    &:after {
      left: 1.2rem;
    }
  }
`

const Input = styled.input`
  position: relative;
  height: 0.875rem;
  width: 2rem;
  opacity: 0;
  z-index: 3;
  cursor: pointer;
`

interface IToggleProps {
  checked: boolean
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  label?: string
}

const Container = styled.label`
  ${fontFamilyPrimary};
  font-size: ${fsXS}px;
  ${fwMediumBold};
  color: ${primaryColor};
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  padding: 10px 0px;
`

const Toggle: FC<IToggleProps> = ({
  checked,
  label,
  onChange = () => undefined,
  ...props
}) => (
  <Container {...props}>
    {label}
    <ToggleImpl checked={checked} onChange={onChange} />
  </Container>
)

export default Toggle
